import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { qk } from './keys';
import type {
  AdminStats,
  AdminUserListItem,
  AuditLogEntry,
  ListAuditQuery,
  ListSessionsQuery,
  ListUsersQuery,
  Paginated,
  Session,
  TranscriptMessage,
  UserProfile,
  UserSession,
  UserRole,
} from '../types/api';

// ------------------------------------------------------------------ stats --

export function useStats() {
  return useQuery({
    queryKey: qk.stats,
    queryFn: () => api.get<AdminStats>('/admin/stats'),
    // The live block (who is online, what is in progress) is the reason to
    // keep this screen open, so it refreshes on its own.
    refetchInterval: 30_000,
  });
}

// ------------------------------------------------------------------ users --

export function useUsers(query: ListUsersQuery) {
  return useQuery({
    queryKey: qk.users(query),
    queryFn: () => api.get<Paginated<AdminUserListItem>>('/admin/users', query),
    // Keeps the previous page on screen while the next one loads, instead of
    // collapsing the table to a spinner on every keystroke or page change.
    placeholderData: (prev) => prev,
  });
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: qk.user(id ?? ''),
    queryFn: () => api.get<AdminUserListItem>(`/admin/users/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * The devices an account has signed in from, newest first and including the
 * sessions that have ended — the history is what shows an account being
 * passed around, and `replaced` is the reason that says so.
 */
export function useUserSessions(id: string | undefined) {
  return useQuery({
    queryKey: qk.userSessions(id ?? ''),
    queryFn: () => api.get<UserSession[]>(`/admin/users/${id}/sessions`),
    enabled: Boolean(id),
  });
}

export function useRevokeUserSession(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.delete<{ success: true }>(`/admin/users/${userId}/sessions/${sessionId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.userSessions(userId) });
      // The flag on the user row counts revocations, so it can move too.
      void queryClient.invalidateQueries({ queryKey: qk.user(userId) });
    },
  });
}

export interface CreateUserInput {
  email: string;
  password: string;
  fullName: string;
  role: UserRole;
  phone?: string;
  timezone?: string;
  hourlyRateCents?: number;
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => api.post<UserProfile>('/admin/users', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.usersAll });
      void qc.invalidateQueries({ queryKey: qk.stats });
      void qc.invalidateQueries({ queryKey: qk.auditAll });
    },
  });
}

export interface UpdateUserInput {
  fullName?: string;
  email?: string;
  avatarUrl?: string;
  phone?: string;
  timezone?: string;
}

export function useUpdateUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateUserInput) =>
      api.patch<UserProfile>(`/admin/users/${userId}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.user(userId) });
      void qc.invalidateQueries({ queryKey: qk.usersAll });
      void qc.invalidateQueries({ queryKey: qk.auditAll });
    },
  });
}

export function useSetUserActive(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ isActive, reason }: { isActive: boolean; reason?: string }) =>
      api.post<UserProfile>(
        `/admin/users/${userId}/${isActive ? 'activate' : 'deactivate'}`,
        { reason },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.user(userId) });
      void qc.invalidateQueries({ queryKey: qk.usersAll });
      void qc.invalidateQueries({ queryKey: qk.stats });
      void qc.invalidateQueries({ queryKey: qk.auditAll });
    },
  });
}

export function useResetPassword(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ newPassword, reason }: { newPassword: string; reason?: string }) =>
      api.post<{ success: true }>(`/admin/users/${userId}/reset-password`, {
        newPassword,
        reason,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.auditAll }),
  });
}

export function useVerifyTutor(tutorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ isVerified, reason }: { isVerified: boolean; reason?: string }) =>
      api.post<UserProfile>(`/admin/tutors/${tutorId}/verify`, { isVerified, reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.user(tutorId) });
      void qc.invalidateQueries({ queryKey: qk.usersAll });
      void qc.invalidateQueries({ queryKey: qk.stats });
      void qc.invalidateQueries({ queryKey: qk.auditAll });
    },
  });
}

// --------------------------------------------------------------- sessions --

export function useAdminSessions(query: ListSessionsQuery) {
  return useQuery({
    queryKey: qk.adminSessions(query),
    queryFn: () => api.get<Paginated<Session>>('/admin/sessions', query),
    placeholderData: (prev) => prev,
  });
}

export function useAdminSession(id: string | undefined) {
  return useQuery({
    queryKey: qk.adminSession(id ?? ''),
    queryFn: () => api.get<Session>(`/admin/sessions/${id}`),
    enabled: Boolean(id),
  });
}

export function useForceCancelSession(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.post<Session>(`/admin/sessions/${sessionId}/cancel`, { reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.adminSessionsAll });
      void qc.invalidateQueries({ queryKey: qk.stats });
      void qc.invalidateQueries({ queryKey: qk.auditAll });
    },
  });
}

export interface ChangeSessionTutorInput {
  id: string;
  tutorId: string;
  reason: string;
}

/**
 * Not parameterised by session id, unlike the cancel hook: the modal behind
 * it opens from a row in the list as well as from one lesson's page, and the
 * id is only known once a row is picked.
 */
export function useChangeSessionTutor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ChangeSessionTutorInput) =>
      api.post<Session>(`/admin/sessions/${id}/tutor`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.adminSessionsAll });
      void qc.invalidateQueries({ queryKey: qk.auditAll });
    },
  });
}

// ----------------------------------------------------- moderation / chat --

export function useTranscript(sessionId: string | undefined) {
  return useQuery({
    queryKey: qk.transcript(sessionId ?? ''),
    queryFn: () => api.get<TranscriptMessage[]>(`/admin/sessions/${sessionId}/messages`),
    enabled: Boolean(sessionId),
  });
}

export function useDeleteMessage(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      api.delete<{ success: true }>(`/admin/messages/${messageId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.transcript(sessionId) });
      void qc.invalidateQueries({ queryKey: qk.auditAll });
    },
  });
}

// -------------------------------------------------------------- audit log --

export function useAuditLog(query: ListAuditQuery) {
  return useQuery({
    queryKey: qk.audit(query),
    queryFn: () => api.get<Paginated<AuditLogEntry>>('/admin/audit-log', query),
    placeholderData: (prev) => prev,
  });
}
